import { action, computed, observable } from 'mobx';
import { ProjectsStore } from './ProjectsStore';
import stores from 'cd-eft/models/Stores';
import { formatRound } from 'cd-eft/utils/mathUtilities';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

import GroupLayer from '@arcgis/core/layers/GroupLayer';
import MapView from '@arcgis/core/views/MapView';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import { putStructureRepOnTop } from 'cd-eft/utils/gisUtils';
import { LinearUnit, constructTerraceLayer } from 'cd-eft/utils/structureLayerUtils';
import { EftProjUserInputController } from 'cd-eft/services/eftPrjAdminApi';
import { terraceTypeOptions } from 'cd-eft/components/Design/Terrace/TerraceInputs/TerraceInputs.labels.config';
import { IProfileSettingsForm, profileSettingsFormDefault } from 'cd-eft/components/Design/Terrace/TerraceDesignProfileView/TerraceDesignProfileView.config';


class TerraceStoreProjectsStore extends ProjectsStore {
  constructor(private terracedesignstore: any) {
    super(terracedesignstore);
  }
}

// Interface for ITerraceVertexParamsDTO, based on BE TerraceVertexParamsDTO
export interface ITerraceVertexParamsDTO {
  reachId: string;
  status: string;
  startStation: string;
  endStation: string;
  cutFill: number;
  groundElev: number;
  channelElev: number;
  stationDist: number;
  grade: number;
  flowQ: number;
  flowQEdited: boolean;
  flowVelocity: number;
  peakFlowDepth: number;
  frontHeight: number;
  frontHeightEdited: boolean;
  bottomWidth: number;
  bottomWidthEdited: boolean;
  estimatedCutSlopeRatio: number;
  simWidth: number;
  designHeight: number;
  liningMaterial: string | null;
  templateName: string | null;
  templateParams: ITemplateParams;
  slopeLimits: ISlopeLimits[];
  drainStationDto: IDrainStationDTO;
  blockStationDto: IBlockStationDTO;
  blockStation: boolean;
  drainStation: boolean
  errorResponse: IErrorResponseDTO;
}

interface IErrorResponseDTO {
  errorCode: string | null;
  errorMessage: string | null;
}

interface ITemplateParams {
  cutSlope: number | '';
  frontSlope: number | '';
  backSlope: number | '';
  bottomWidth: number | '';
  frontHeight: number | '';
  topWidth: number | '';
  backSlopeMode: number;
  cutSlopeMode: number;
  frontSlopeMode: number;
}

export interface ISlopeLimits {
  slopeName: string;
  slopeMode: string;
  slopeValue: number | '';
  isLimitEnforced: number;
  limitingMode: string;
  slopeLimit: number | '';
}

// Interface for the drain station DTO
export interface IDrainStationDTO {
  drainName: string | null;
  drainFraction: number;
  drainOffset: number;
  ugoAndInlet: string | null;
  location: IInputPoint;
}

// Interface for the block station DTO
export interface IBlockStationDTO {
  sideSlopeMode: number;
  sideSlope: number;
  limits: number;
  topWidth: number;
  location: IInputPoint;
  isSplitSubbasin: boolean;
  blockHeight: string | null;
}

// Interface for input points
interface IInputPoint {
  id: number;
  pointName: string | null;
  description: string | null;
  tinnable: boolean;
  visible: boolean;
  wkid: number;
  x: number;
  y: number;
  z: number;
}

//intial version and it need updates
interface ITerraceDesignObject {
  beginningX: number;
  beginningY: number;
  startGradeLength: number;
  endGradeLength: number;
  startGuardrail: number;
  endGuardrail: number;
  peakFlow: number;
  terraceDesignStations: ITerraceVertexParamsDTO[];
  alignedElementDesignParametersId: number;
  alignedFeatureName: string;
  editingStatus: string;
  surfaceDefId: number;
  surfaceDefTitle: string;
  sessionId: number;
  disableCalculateButtonDesignPage: boolean;
  DisableSaveButtonDesignPage: boolean;
  hasTerraceCalculations: boolean;
}

interface ITerraceGroupTracker {
  groupCount: number;
  currentObject: ITerraceDesignObject;
}

export type UIState = 'TerraceInputSave' | 'TerraceInputProceed' | 'TerraceTemplateSave' | 'TerraceTemplateProceed' | 'ChannelDesignSave' | 'ChannelDesignCalculate' | 'ChannelDesignSurface' | undefined;

export const TerraceUiState = {
  TerraceInputSave: {
    string: 'TerraceInputSave',
    loadedPage: 'input',
    number: 11
  },
  TerraceInputProceed: {
    string: 'TerraceInputProceed',
    loadedPage: 'template',
    number: 12
  },
  TerraceTemplateSave: {
    string: 'TerraceTemplateSave',
    loadedPage: 'template',
    number: 13
  },
  TerraceTemplateProceed: {
    string: 'TerraceTemplateProceed',
    loadedPage: 'design',
    number: 14
  },
  // design page
  ChannelDesignSave: {
    string: 'ChannelDesignSave',
    loadedPage: 'design',
    number: 15
  },
  ChannelDesignCalculate: {
    string: 'ChannelDesignCalculate',
    loadedPage: 'design',
    number: 16
  },
  ChannelDesignSurface: {
    string: 'ChannelDesignSurface',
    loadedPage: 'design',
    number: 17
  }
}

export interface IReachTemplateForm {
  templateName?: string;
  bottomWidth: number | '';
  frontHeight: number | '';
  topWidth: number | '';
  slopeLimits: ISlopeLimits[];
  // cutSlope: ISlopeLimits; index 0 as a standard in FE
  // frontSlope: ISlopeLimits; index 1 as a standard in FE
  // backSlope: ISlopeLimits; index 2 as a standard in FE
}

/**
 * Enum for use in terraceChannelTemplateJson
 * Integer is determined by the Reach Template Form Mode dropdown selection.
 */
export enum SlopeModeChannelEnum {
  RATIO_BASED = 1,
  WIDTH_BASED = 2,
}
/**
 * Enum used to avoid ternary expression mode === 'WIDTH_BASED' ? 'RATIO_BASED' : 'WIDTH_BASED'
 * The limiting Mode is the opposite of the related Mode selection
 */
export enum SlopeModeToLimitEnum {
  WIDTH_BASED = 'RATIO_BASED',
  RATIO_BASED = 'WIDTH_BASED'
}


export const reachTemplateFormDefault: IReachTemplateForm = {
  templateName: "",
  bottomWidth: 1,
  frontHeight: 1,
  topWidth: 1.00,
  slopeLimits: [
    {
      slopeName: 'cutSlope',
      slopeMode: 'WIDTH_BASED',
      slopeValue: 5.0,
      isLimitEnforced: 1,
      limitingMode: 'RATIO_BASED',
      slopeLimit: 50
    },
    {
      slopeName: 'frontSlope',
      slopeMode: 'RATIO_BASED',
      slopeValue: 5.0,
      isLimitEnforced: 1,
      limitingMode: 'WIDTH_BASED',
      slopeLimit: 10
    },
    {
      slopeName: 'backSlope',
      slopeMode: 'WIDTH_BASED',
      slopeValue: 5,
      isLimitEnforced: 0,
      limitingMode: 'RATIO_BASED',
      slopeLimit: 1
    }
  ]
};

interface TerraceSummaryInfo {
  templateName: string;
  channelLength: number;
  terraceNameExists: boolean;
}

export class TerraceDesignStore {
  constructor(private appStore: any) {
    this.projectsStore = new TerraceStoreProjectsStore(this);
    this.init();
  }

  @observable projectsStore: TerraceStoreProjectsStore;
  @observable terraceGroupsOriginals: ITerraceGroupTracker[] = []; // Used to save pre-PEM Terrace groups
  @observable addTerraceClicked: boolean = false;
  @observable terraceDesignObject!: ITerraceDesignObject;
  @observable checkedRowStations: any[] = [];
  @observable newTerraces: any[] = []; // for highlighting terraces in summary after splitting - do not put in init()

  @observable deleteStationFromTable: boolean = false;
  @observable gradeEdited = false;
  @observable stationLengthEdited = false;
  @observable channelStartGradeLengthChangedGlobal: boolean = false;
  @observable channelEndGradeLengthChangedGlobal: boolean = false;
  @observable terraceInputsChanged: boolean = false;
  @observable terraceLayers: GroupLayer[] = [];
  @observable hasCalculatedData: boolean = true;
  @observable terraceHistory: any
  @observable summaryTerraceTypes: any[] = []; // Don't include in reset function

  @observable summaryInfo: TerraceSummaryInfo = {
    templateName: '',
    channelLength: 0,
    terraceNameExists: false
  }

  @observable uiState: UIState = undefined;
  @observable closeMatchModal: boolean = false; // Triggers closeMatchVertexModal on change
  // ****** Saved from Inputs *******
  // Terrace Design is using terraceDesignObject.alignedElementDesignParametersId. Indiv. variable removed to avoid confusion.
  // @observable alignedDesignElementId: number = 0;// 12826 for testing without new designs;
  @observable terraceType: number = 0;
  @observable alignmentDesignElementJsonId: number = 0;
  @observable notifications = {
    unsavedEditsClosePage: ''
  }

  // ********* For profiles *********
  @observable waterLevelData: any = [];
  @observable constructedProfileStations: any = [];
  @observable settledProfileStations: any = [];
  @observable savedProfileSettingsForm: IProfileSettingsForm = profileSettingsFormDefault;
  @computed get uiSettingsDto() {
    return {
      fineGrid: this.savedProfileSettingsForm.input.fineGrid,
      sampleInterval: this.savedProfileSettingsForm.input.sampleInterval,
      verticalInterval: this.savedProfileSettingsForm.input.verticalInterval,
      showCoord: this.savedProfileSettingsForm.checked.showFineGrid
    }
  }

  @observable defaultTemplateParams: ITemplateParams = {
    cutSlope: 0,
    frontSlope: 0,
    backSlope: 0,
    bottomWidth: 0,
    frontHeight: 0,
    topWidth: 0,
    backSlopeMode: 0,
    cutSlopeMode: 0,
    frontSlopeMode: 0
  };

  @observable defaultSlopeLimits: ISlopeLimits[] =
    [
      {
        slopeName: '',
        slopeMode: '',
        slopeValue: 0,
        isLimitEnforced: 1,
        limitingMode: '',
        slopeLimit: 0
      }
    ];

  @observable hydroInputValues = {
    peakFlow: 0,
    watershedArea: 0,
    watershedCN: 0,
    watershedLength: 0,
    watershedSlope: 0,
    runoffQ: 0,
  };

  initialTerraceDesignStation: ITerraceVertexParamsDTO = {
    reachId: '',
    status: '',
    startStation: '',
    endStation: '',
    cutFill: 0,
    groundElev: 0,
    channelElev: 0,
    stationDist: 0,
    grade: 0,
    flowQ: 0,
    flowQEdited: false,
    flowVelocity: 0,
    peakFlowDepth: 0,
    frontHeight: 0,
    frontHeightEdited: false,
    bottomWidth: 0,
    bottomWidthEdited: false,
    estimatedCutSlopeRatio: 0,
    simWidth: 0,
    designHeight: 0,
    liningMaterial: null,
    templateName: null,
    templateParams: {
      cutSlope: 0,
      frontSlope: 0,
      backSlope: 0,
      bottomWidth: 0,
      frontHeight: 0,
      topWidth: 0,
      backSlopeMode: 0,
      cutSlopeMode: 0,
      frontSlopeMode: 0
    },
    slopeLimits: [
      {
        slopeName: 'cutSlope',
        slopeMode: 'WIDTH_BASED',
        slopeValue: 5.0,
        isLimitEnforced: 1,
        limitingMode: 'RATIO_BASED',
        slopeLimit: 50
      },
      {
        slopeName: 'frontSlope',
        slopeMode: 'RATIO_BASED',
        slopeValue: 5.0,
        isLimitEnforced: 1,
        limitingMode: 'WIDTH_BASED',
        slopeLimit: 50
      },
      {
        slopeName: 'backSlope',
        slopeMode: 'WIDTH_BASED',
        slopeValue: 5.0,
        isLimitEnforced: 0,
        limitingMode: 'RATIO_BASED',
        slopeLimit: 50
      }
    ],
    drainStationDto: {
      drainName: null,
      drainFraction: 0,
      drainOffset: 0,
      ugoAndInlet: null,
      location: {
        id: 0,
        pointName: null,
        description: null,
        tinnable: true,
        visible: true,
        wkid: 0,
        x: 0,
        y: 0,
        z: 0,
      },
    },
    blockStationDto: {
      sideSlopeMode: 1,
      sideSlope: 0,
      limits: 0,
      topWidth: 0,
      location: {
        id: 0,
        pointName: null,
        description: null,
        tinnable: true,
        visible: true,
        wkid: 0,
        x: 0,
        y: 0,
        z: 0,
      },
      isSplitSubbasin: false,
      blockHeight: null,
    },
    blockStation: false,
    drainStation: false,
    errorResponse: {
      errorCode: null,
      errorMessage: null,
    },
  };

  initialTerraceDesignObject: ITerraceDesignObject = {
    beginningX: 0,
    beginningY: 0,
    startGradeLength: 0,
    endGradeLength: 0,
    startGuardrail: 0,
    endGuardrail: 0,
    peakFlow: 0,
    terraceDesignStations: [],
    alignedElementDesignParametersId: 0,
    alignedFeatureName: '',
    editingStatus: '',
    surfaceDefId: 0,
    surfaceDefTitle: '',
    sessionId: 0,
    disableCalculateButtonDesignPage: false, // control generate surface button in waterway design
    DisableSaveButtonDesignPage: false, // control save&calculate button in waterway design
    hasTerraceCalculations: false, // control generate surface button in waterway design
  };

  // saving initial template data for comparing with new values and reset if needed
  @observable initialTerraceTemplate: IReachTemplateForm = JSON.parse(JSON.stringify(reachTemplateFormDefault));

  @action.bound resetTerraceInitialTemplate = () => {
    this.initialTerraceTemplate = JSON.parse(JSON.stringify(reachTemplateFormDefault));
  }

  @observable terraceReport: any = {};
  @observable displayResultBanner: boolean = false;
  @observable generateTerraceSurface: boolean = false;
  @observable terraceSidebarIsCollapsed: boolean = true;
  @action.bound setCollapsed = () => this.terraceSidebarIsCollapsed = !this.terraceSidebarIsCollapsed;
  @action.bound closeAction() {
    this.terraceHistory?.push(`/design/${stores.AppStore.designStore.projectsStore.currentProjectId}/terrace`);
  }
  @action.bound handlecloseAction() {
    if (this.terraceInputsChanged) {
      MessageCenter.addWarning(stores.AppStore.terraceDesignStore.notifications?.unsavedEditsClosePage, [
        {
          callback: () => {
            MessageCenter.clearMessages();
          },
          text: 'Cancel',
        },
        {
          callback: () => {
            MessageCenter.clearMessages();
            stores.AppStore.terraceDesignStore.reset()
            this.closeAction()
          },
          text: 'OK',
        },
      ], 'Common_13');
    } else {
      MessageCenter.clearMessages();
      stores.AppStore.terraceDesignStore.reset()
      this.closeAction()
    }
  }
  @action init() {
    this.resetCurrentObject();
    this.resetTerraceInitialTemplate();
    this.defaultTemplateParams = {
      cutSlope: 0,
      frontSlope: 0,
      backSlope: 0,
      bottomWidth: 0,
      frontHeight: 0,
      topWidth: 0,
      backSlopeMode: 0,
      cutSlopeMode: 0,
      frontSlopeMode: 0
    };
    this.defaultSlopeLimits = [
      {
        slopeName: '',
        slopeMode: '',
        slopeValue: 0,
        isLimitEnforced: 1,
        limitingMode: '',
        slopeLimit: 0
      }
    ];
    this.terraceGroupsOriginals = [];
    this.channelStartGradeLengthChangedGlobal = false;
    this.channelEndGradeLengthChangedGlobal = false;
    this.waterLevelData = [];
    this.constructedProfileStations = [];
    this.settledProfileStations = [];
    this.alignmentDesignElementJsonId = 0;
    this.terraceType = 0;
    this.terraceReport = {};
    this.displayResultBanner = false;
    this.generateTerraceSurface = false;
    this.hasCalculatedData = true;
    this.terraceSidebarIsCollapsed = true;
    this.terraceInputsChanged = false;
    this.summaryInfo = {
      templateName: '',
      channelLength: 0,
      terraceNameExists: false
    }
    this.uiState = undefined;
  }

  @action resetCurrentObject() {
    this.terraceDesignObject = { ...this.initialTerraceDesignObject };
  }

  @action.bound reset() {
    this.init();
  }

  @action.bound async handleUiStateUpdate({ uiStateType }) {
    let isSuccess = false;
    try {
      const response = await EftProjUserInputController.saveDesignUiStateUsingPOST({
        alignedElementUiStateDto: {
          alignmentElementId: +this.terraceDesignObject.alignedElementDesignParametersId,
          alignmentId: this.alignmentDesignElementJsonId,
          channelShape: terraceTypeOptions.find((type) => +type.value === +this.terraceType)?.name,
          designElementTypeId: +this.terraceType,
          endDistance: 0.0,
          peakFlow: 0.0,
          startDistance: 0.0,
          tableView: TerraceUiState[uiStateType].number >= 15 ? 'C, S, R, T' : 'C',
          uiSettingsDto: this.uiSettingsDto,
        },
        sessionUuid: stores.AppStore.designStore.projectsStore.currentSessionId,
        uiStateType: uiStateType,
      });
      if (response && response.data && response.data.errorCode === "Success") {
        isSuccess = true;
        this.uiState = response.data.uiStateType! as UIState
      }
    } catch (e) {
      console.error(e)
    }
    return isSuccess

  }
  // reusable onblur function in TerraceInnputs and TerraceCalculate
  @action.bound handleInputsOnBlur = async (e, section, blockErrorCode = false, configObject, formsValidationObject, redHighlightingObject, onChangeFunction, blockRedHighlighting = false) => {
    const { name, value } = e.target;

    const inputConfig = configObject[section]?.[name];
    if (!inputConfig) {
      console.error(`No input configuration found for section "${section}" and input "${name}".`);
      return;
    }

    const {
      inputType,
      errorCode,
      required = true, // Default to true if undefined
      min,
      max,
      label,
      decimalPlaces = 2 //  Default to 2 if undefined
    } = inputConfig;

    // Convert value to a number if applicable
    const numericValue = formatRound(parseFloat(value), decimalPlaces);

    if (inputType === 'number' && !isNaN(numericValue) && !blockErrorCode) {
      const element = { target: { name: name, value: numericValue } }
      onChangeFunction(element, section)
    }

    // Skip validation if not required
    if (!required || inputType === 'title') return;


    let shouldTriggerError = false;
    if (value === '') {
      shouldTriggerError = true;
    } else if (
      inputType === 'number' &&
      !isNaN(numericValue) &&
      ((min !== undefined && numericValue < min) || (max !== undefined && numericValue > max))
    ) {
      shouldTriggerError = true;
    }

    if (shouldTriggerError) {

      formsValidationObject[section][name] = false;

      if (errorCode && !blockErrorCode) {
        const processedLabel = label && errorCode.startsWith("Common")
          ? label.replace(/ \([^)]*\)/, '')
          : undefined;
        await MessageCenter.addNotificationFromCode(errorCode, undefined, processedLabel);
      }

      if (!blockRedHighlighting) {
        redHighlightingObject[section][name] = true;
      }

    } else {

      formsValidationObject[section][name] = true;

      if (!blockRedHighlighting) {
        redHighlightingObject[section][name] = false;
      }

    }
  }

  @action addTerraceToMap(terrace: any, mapView: MapView, id: number, title: string, fromRefresh: boolean = false): GroupLayer {
    const groupLayerId = `Terrace${id}`;
    const deleteLayer = this.terraceLayers.find(lyr => lyr.id === groupLayerId);
    if (deleteLayer && !fromRefresh) {
      mapView.map.remove(deleteLayer);
      deleteLayer.destroy();

      this.terraceLayers = this.terraceLayers.filter(lyr => lyr.id !== groupLayerId);
    }

    const newLayer = this.createTerraceLayer(terrace, mapView, groupLayerId, title, id);
    mapView.map.add(newLayer);
    putStructureRepOnTop(mapView, groupLayerId);
    putStructureRepOnTop(mapView, 'alignment_sketch');
    this.terraceLayers.push(newLayer);
    return newLayer;
  }

  private createTerraceLayer(terrace: any, mapView: MapView, groupLayerId: string, title: string, alignedElementId): GroupLayer {
    const inputSR: SpatialReference = stores.AppStore.projectsLibraryStore.projectSpatialReference!;
    const outputSR: SpatialReference = mapView.spatialReference;
    const inputUnit: LinearUnit = 'intl-feet';
    const outputUnit: LinearUnit = 'meters';
    const terraceLayer = constructTerraceLayer(terrace, inputSR, outputSR, inputUnit, outputUnit, groupLayerId, title, alignedElementId);
    return terraceLayer;
  }

  /**
   * Convert Slope mode integer to corresponding string
   * @param slopeMode Integer 1 or 2 for RATIO_BASED or WIDTH_BASED respectively
   * @returns 'RATIO_BASED' or 'WIDTH_BASED'
   */
  convertSlopeNumberToString(slopeMode: number): 'RATIO_BASED' | 'WIDTH_BASED' {
    return slopeMode === 2 ? 'WIDTH_BASED' : 'RATIO_BASED';
  }
}
